/**
 * Transaction templates (`tx_template` table).
 *
 * `template_json` stores a `TxTemplatePayload` (same payload used by
 * recurring rules): a partial transaction draft with amounts in
 * minor units. When parsing, ALWAYS validate with `parseTxTemplate`.
 */
import { asc, eq, type PowerSyncDrizzleDB, tx_template } from "@cashsync/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import type { TransactionDraft } from "./mutations";
import type { TxTemplatePayload } from "./recurring-engine";

export interface TxTemplateRow {
  id: string;
  name: string;
  sort_order: number;
  /** TxTemplatePayload JSON. */
  template_json: string;
}

export interface TxTemplateDraft {
  name: string;
  sortOrder?: number;
  template: TxTemplatePayload;
}

/** User templates ordered by sort_order. */
export function getTxTemplatesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(tx_template)
    .where(eq(tx_template.user_id, userId))
    .orderBy(asc(tx_template.sort_order), asc(tx_template.created_at));
}

function isTxTemplatePayload(value: unknown): value is TxTemplatePayload {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate: Partial<TxTemplatePayload> = value;
  return (
    (candidate.kind === "expense" ||
      candidate.kind === "income" ||
      candidate.kind === "transfer") &&
    typeof candidate.amount === "number" &&
    typeof candidate.currency === "string" &&
    typeof candidate.walletId === "string"
  );
}

/**
 * Parses template_json safely (returns null instead of throwing).
 * Tolerant variant of recurring-engine's strict `parseTxTemplate`.
 */
export function parseTxTemplateSafe(templateJson: string): TxTemplatePayload | null {
  try {
    const parsed: unknown = JSON.parse(templateJson);
    return isTxTemplatePayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Converts template payload into a `TransactionDraft` ready for
 * `createTransactionMutation` (date is chosen by caller, usually
 * "now" when using quick-add).
 */
export function templateToTransactionDraft(
  template: TxTemplatePayload,
  occurredAtISO: string,
): TransactionDraft {
  return {
    kind: template.kind,
    amount: template.amount,
    currency: template.currency,
    walletId: template.walletId,
    toWalletId: template.toWalletId ?? null,
    toAmount: template.toAmount ?? null,
    feeAmount: template.feeAmount ?? null,
    categoryId: template.categoryId ?? null,
    note: template.note ?? null,
    payee: template.payee ?? null,
    projectId: template.projectId ?? null,
    occurredAtISO,
  };
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createTxTemplateMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: TxTemplateDraft,
): Promise<string> {
  const name = draft.name.trim();
  if (!name) {
    throw new Error("The template needs a name");
  }
  const id = generateId();
  const now = nowISO();
  await db.insert(tx_template).values({
    id,
    user_id: userId,
    name,
    sort_order: draft.sortOrder ?? 0,
    template_json: JSON.stringify(draft.template),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateTxTemplateMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string; sortOrder: number; template: TxTemplatePayload }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const set: Partial<{
    name: string;
    sort_order: number;
    template_json: string;
  }> = {};
  if (updates.name !== undefined) {
    set.name = updates.name.trim();
  }
  if (updates.sortOrder !== undefined) {
    set.sort_order = updates.sortOrder;
  }
  if (updates.template !== undefined) {
    set.template_json = JSON.stringify(updates.template);
  }
  await patchRowVerified(db, tx_template, tx_template.id, id, set, options);
}

/** Physical deletion: templates do not participate in history logs. */
export async function deleteTxTemplateMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(tx_template).where(eq(tx_template.id, id));
}
